import { useState, useEffect, useCallback } from 'react';
import { Key, Plus, RefreshCw, Trash2, RotateCcw, Search, Copy, Check, Shield, Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { PageShell, Modal, FG, INP, SEL, Row2, Row3, BtnPrimary, BtnGhost, BtnDanger, Spinner, fmtINR, fmtDate, useBanner, useWalletUser, EmptyState } from './WalletShared';
import walletController from '../../controllers/walletController';
import invoiceController from '../../controllers/invoiceController';

// ─── Key status config ────────────────────────────────────────────────────────
const KEY_STATUS_CFG = {
  pending:  { color:'#92400e', bg:'#fef3c7', label:'Pending',  Icon:Clock },
  active:   { color:'#15803d', bg:'#dcfce7', label:'Active',   Icon:CheckCircle },
  expired:  { color:'#475569', bg:'#f1f5f9', label:'Expired',  Icon:XCircle },
  revoked:  { color:'#b91c1c', bg:'#fef2f2', label:'Revoked',  Icon:AlertTriangle },
};

const KEY_TYPE_LABELS = {
  api_key:        'API Key',
  license_key:    'License Key',
  activation_key: 'Activation Key',
  trial_key:      'Trial Key',
};

// ─── Copy to clipboard ────────────────────────────────────────────────────────
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} style={{ background:'none', border:'none', cursor:'pointer', color: copied?'#15803d':'#94a3b8', padding:4, display:'flex', alignItems:'center' }}>
      {copied ? <Check size={13}/> : <Copy size={13}/>}
    </button>
  );
};

// ─── Generate Key Modal ───────────────────────────────────────────────────────
const GenerateKeyModal = ({ onGenerate, onClose, isSaving, userId }) => {
  const [form, setForm] = useState({
    keyType:       'api_key',
    label:         '',
    validityDays:  365,
    maxUsage:      '',
    scope:         '',
    environment:   'production',
    // Invoice fields
    planName:      '',
    amount:        0,
    billingCycle:  'one_time',
    dueDate:       new Date(Date.now()+7*24*60*60*1000).toISOString().split('T')[0],
    paymentMethod: 'upi',
    taxAmount:     0,
    notes:         '',
  });
  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));

  // Auto-calculate total
  const totalAmount = Number(form.amount) + Number(form.taxAmount);

  return (
    <Modal title="Generate New Key & Create Invoice" onClose={onClose} maxWidth={640}
      footer={
        <>
          <BtnGhost onClick={onClose}>Cancel</BtnGhost>
          <BtnPrimary onClick={() => onGenerate(form, totalAmount)} disabled={isSaving||!form.label||!form.dueDate}>
            {isSaving ? <><Spinner size={13}/>Generating…</> : <><Key size={13}/>Generate Key & Invoice</>}
          </BtnPrimary>
        </>
      }>

      {/* Key details */}
      <div style={{ background:'#f8fafc', borderRadius:8, padding:'12px 14px' }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:10 }}>Key Details</div>
        <Row2>
          <FG label="Key Type *">
            <SEL value={form.keyType} onChange={e => f('keyType',e.target.value)}>
              {Object.entries(KEY_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </SEL>
          </FG>
          <FG label="Environment">
            <SEL value={form.environment} onChange={e => f('environment',e.target.value)}>
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </SEL>
          </FG>
        </Row2>
        <FG label="Label / Description *">
          <INP value={form.label} onChange={e => f('label',e.target.value)} placeholder="e.g. Production API Key for App v2"/>
        </FG>
        <Row3>
          <FG label="Validity (days)" hint="0 = no expiry">
            <INP type="number" min="0" value={form.validityDays} onChange={e => f('validityDays',Number(e.target.value))}/>
          </FG>
          <FG label="Max Usage" hint="Blank = unlimited">
            <INP type="number" min="0" value={form.maxUsage} onChange={e => f('maxUsage',e.target.value)} placeholder="Unlimited"/>
          </FG>
          <FG label="Scope (comma-sep)">
            <INP value={form.scope} onChange={e => f('scope',e.target.value)} placeholder="read,write"/>
          </FG>
        </Row3>
      </div>

      {/* Invoice details */}
      <div style={{ background:'#eff6ff', borderRadius:8, padding:'12px 14px' }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:10 }}>Invoice Details</div>
        <Row2>
          <FG label="Plan / Product Name">
            <INP value={form.planName} onChange={e => f('planName',e.target.value)} placeholder="e.g. API Key — Annual"/>
          </FG>
          <FG label="Billing Cycle">
            <SEL value={form.billingCycle} onChange={e => f('billingCycle',e.target.value)}>
              <option value="one_time">One Time</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </SEL>
          </FG>
        </Row2>
        <Row3>
          <FG label="Amount (₹) *">
            <INP type="number" min="0" value={form.amount} onChange={e => f('amount',e.target.value)} placeholder="0"/>
          </FG>
          <FG label="Tax (₹)">
            <INP type="number" min="0" value={form.taxAmount} onChange={e => f('taxAmount',e.target.value)} placeholder="0"/>
          </FG>
          <FG label="Due Date *">
            <INP type="date" value={form.dueDate} onChange={e => f('dueDate',e.target.value)}/>
          </FG>
        </Row3>
        <Row2>
          <FG label="Payment Method">
            <SEL value={form.paymentMethod} onChange={e => f('paymentMethod',e.target.value)}>
              {['upi','card','bank_transfer','wallet','cash'].map(m => <option key={m} value={m}>{m.replace('_',' ').toUpperCase()}</option>)}
            </SEL>
          </FG>
          <FG label="Notes">
            <INP value={form.notes} onChange={e => f('notes',e.target.value)} placeholder="Optional notes"/>
          </FG>
        </Row2>
        {/* Summary */}
        <div style={{ display:'flex', justifyContent:'flex-end', fontSize:13, fontWeight:700, color:'#1e293b', marginTop:8, borderTop:'1px solid #dbeafe', paddingTop:8 }}>
          Invoice Total: {fmtINR(totalAmount)}
        </div>
      </div>
    </Modal>
  );
};

// ─── Renew Key Modal ──────────────────────────────────────────────────────────
const RenewModal = ({ keyItem, onRenew, onClose, isSaving, userId }) => {
  const [form, setForm] = useState({
    validityDays: 365,
    amount:       0,
    taxAmount:    0,
    dueDate:      new Date(Date.now()+7*24*60*60*1000).toISOString().split('T')[0],
    paymentMethod:'upi',
    notes:        '',
  });
  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));
  const total = Number(form.amount)+Number(form.taxAmount);

  return (
    <Modal title={`Renew Key — ${keyItem?.label||keyItem?.licenseKey}`} onClose={onClose}
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary onClick={() => onRenew(form, total)} disabled={isSaving}>{isSaving?<><Spinner size={13}/>Renewing…</>:<><RotateCcw size={13}/>Renew & Invoice</>}</BtnPrimary></>}>
      <Row2>
        <FG label="New Validity (days)"><INP type="number" min="1" value={form.validityDays} onChange={e => f('validityDays',Number(e.target.value))}/></FG>
        <FG label="Due Date *"><INP type="date" value={form.dueDate} onChange={e => f('dueDate',e.target.value)}/></FG>
      </Row2>
      <Row2>
        <FG label="Renewal Amount (₹)"><INP type="number" min="0" value={form.amount} onChange={e => f('amount',e.target.value)}/></FG>
        <FG label="Tax (₹)"><INP type="number" min="0" value={form.taxAmount} onChange={e => f('taxAmount',e.target.value)}/></FG>
      </Row2>
      <FG label="Payment Method">
        <SEL value={form.paymentMethod} onChange={e => f('paymentMethod',e.target.value)}>
          {['upi','card','bank_transfer','wallet','cash'].map(m => <option key={m} value={m}>{m.replace('_',' ').toUpperCase()}</option>)}
        </SEL>
      </FG>
      <FG label="Notes"><INP value={form.notes} onChange={e => f('notes',e.target.value)} placeholder="Renewal notes"/></FG>
      <div style={{ textAlign:'right', fontSize:13, fontWeight:700, color:'#1e293b' }}>Invoice Total: {fmtINR(total)}</div>
    </Modal>
  );
};

// ─── Revoke Confirm Modal ─────────────────────────────────────────────────────
const RevokeModal = ({ keyItem, onRevoke, onClose, isSaving }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal title="Revoke Key" onClose={onClose} maxWidth={400}
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnDanger onClick={() => onRevoke(reason)} disabled={isSaving}>{isSaving?<><Spinner size={12}/>Revoking…</>:<><Trash2 size={12}/>Revoke</>}</BtnDanger></>}>
      <p style={{ fontSize:13, color:'#475569', margin:0, lineHeight:1.5 }}>
        Revoking <strong>{keyItem?.label||keyItem?.licenseKey}</strong> will immediately invalidate it. This cannot be undone.
      </p>
      <FG label="Reason (optional)">
        <INP value={reason} onChange={e => setReason(e.target.value)} placeholder="Security concern, expired usage, etc."/>
      </FG>
    </Modal>
  );
};

// ─── Main KeyWallet Page ──────────────────────────────────────────────────────
const KeyWallet = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [keys,       setKeys]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({ page:1, pages:1, total:0 });
  const [filters,    setFilters]    = useState({ page:1, limit:12, status:'', keyType:'' });
  const [search,     setSearch]     = useState('');
  const [modal,      setModal]      = useState(null);  // 'generate' | 'renew' | 'revoke'
  const [activeKey,  setActiveKey]  = useState(null);
  const [isSaving,   setIsSaving]   = useState(false);

  const load = useCallback(async (silent=false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const f = { ...filters };
    if (search) f.search = search;
    const res = await walletController.getKeyWallet(userId, f);
    if (res.success) { setKeys(res.data); setPagination(res.pagination||{}); }
    else notify('error', res.message);
    setLoading(false); setRefreshing(false);
  }, [userId, filters, search]);

  useEffect(() => { load(false); }, [load]);

  // ── Generate: create key + create invoice ──────────────────────────────────
  const handleGenerate = async (form, totalAmount) => {
    setIsSaving(true);
    // 1. Generate key
    const keyRes = await walletController.generateKey(userId, {
      keyType:      form.keyType,
      label:        form.label,
      validityDays: form.validityDays,
      maxUsage:     form.maxUsage ? Number(form.maxUsage) : null,
      scope:        form.scope.split(',').map(s=>s.trim()).filter(Boolean),
      environment:  form.environment,
    });
    if (!keyRes.success) { setIsSaving(false); notify('error', keyRes.message); return; }

    // 2. Create key invoice
    const startDate  = new Date();
    const endDate    = form.validityDays > 0
      ? new Date(Date.now() + form.validityDays*24*60*60*1000)
      : null;

    const invRes = await invoiceController.createKeyInvoice({
      userId,
      keyDetails: {
        keyId:            keyRes.data?._id,
        keyType:          form.keyType,
        licenseKey:       keyRes.data?.licenseKey,
        validFrom:        startDate.toISOString(),
        validTo:          endDate?.toISOString(),
        maxUsage:         form.maxUsage ? Number(form.maxUsage) : null,
        activationStatus: 'active',
        scope:            form.scope.split(',').map(s=>s.trim()).filter(Boolean),
        environment:      form.environment,
      },
      subtotal:      Number(form.amount),
      discountAmount:0,
      taxLines:      form.taxAmount>0 ? [{ taxName:'GST', rate:18, amount:Number(form.taxAmount) }] : [],
      taxTotal:      Number(form.taxAmount),
      totalAmount,
      currency:      'INR',
      dueDate:       form.dueDate,
      paymentMethod: form.paymentMethod,
      notes:         form.notes||`Key: ${form.label} (${KEY_TYPE_LABELS[form.keyType]})`,
      status:        'sent',
    });

    setIsSaving(false);
    if (invRes.success) {
      notify('success', `Key generated! Invoice #${invRes.data?.invoiceNumber} created.`);
      setModal(null);
      load(true);
    } else {
      notify('success', `Key generated but invoice failed: ${invRes.message}`);
      setModal(null);
      load(true);
    }
  };

  // ── Renew: renew key + create renewal invoice ──────────────────────────────
  const handleRenew = async (form, totalAmount) => {
    setIsSaving(true);
    const renewRes = await walletController.renewKey(userId, activeKey._id, {
      validityDays:  form.validityDays,
      paymentMethod: form.paymentMethod,
      notes:         form.notes,
    });
    if (!renewRes.success) { setIsSaving(false); notify('error', renewRes.message); return; }

    const newEndDate = new Date(Date.now() + form.validityDays*24*60*60*1000);
    const invRes = await invoiceController.createKeyInvoice({
      userId,
      keyDetails: {
        keyId:            activeKey._id,
        keyType:          activeKey.keyType,
        licenseKey:       activeKey.licenseKey,
        validFrom:        new Date().toISOString(),
        validTo:          newEndDate.toISOString(),
        activationStatus: 'active',
      },
      subtotal:      Number(form.amount),
      discountAmount:0,
      taxLines:      form.taxAmount>0 ? [{ taxName:'GST', rate:18, amount:Number(form.taxAmount) }] : [],
      taxTotal:      Number(form.taxAmount),
      totalAmount,
      currency:      'INR',
      dueDate:       form.dueDate,
      paymentMethod: form.paymentMethod,
      notes:         form.notes||`Key Renewal: ${activeKey.label}`,
      status:        'sent',
    });

    setIsSaving(false);
    notify('success', invRes.success ? `Key renewed! Invoice #${invRes.data?.invoiceNumber} created.` : `Key renewed! Invoice creation failed.`);
    setModal(null); setActiveKey(null); load(true);
  };

  // ── Revoke ────────────────────────────────────────────────────────────────
  const handleRevoke = async (reason) => {
    setIsSaving(true);
    const res = await walletController.revokeKey(userId, activeKey._id, reason);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); setActiveKey(null); load(true); }
    else notify('error', res.message);
  };

  const setFilter = (k,v) => setFilters(p => ({ ...p, [k]:v, page:1 }));

  return (
    <PageShell title="Key Wallet" subtitle="Manage API keys, license keys, and activation keys" icon={Key}
      banner={banner} onCloseBanner={clearBanner}
      actions={
        <>
          <button onClick={() => load(true)} disabled={refreshing} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12, cursor:'pointer', color:'#475569' }}>
            <RefreshCw size={13} style={{ animation:refreshing?'spin 1s linear infinite':'none' }}/> Refresh
          </button>
          <button onClick={() => { setActiveKey(null); setModal('generate'); }} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 14px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
            <Plus size={14}/>Generate Key
          </button>
        </>
      }>

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <Search size={14} color="#94a3b8" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key==='Enter' && load(false)}
            placeholder="Search keys…"
            style={{ width:'100%', padding:'8px 8px 8px 32px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' }}/>
        </div>
        <select value={filters.keyType} onChange={e => setFilter('keyType',e.target.value)}
          style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, background:'#fff', outline:'none' }}>
          <option value="">All Types</option>
          {Object.entries(KEY_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilter('status',e.target.value)}
          style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, background:'#fff', outline:'none' }}>
          <option value="">All Status</option>
          {Object.entries(KEY_STATUS_CFG).map(([v,c]) => <option key={v} value={v}>{c.label}</option>)}
        </select>
      </div>

      {/* Keys grid */}
      {loading
        ? <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:300, gap:10, color:'#64748b' }}><Spinner size={26}/><span>Loading keys…</span></div>
        : keys.length===0
          ? <EmptyState icon={Key} message="No keys found"
              action={<button onClick={() => setModal('generate')} style={{ padding:'8px 18px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>Generate First Key</button>}/>
          : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
              {keys.map(k => {
                const cfg = KEY_STATUS_CFG[k.activationStatus||'pending'];
                const CfgIcon = cfg.Icon;
                const isActive  = k.activationStatus === 'active';
                const isRevoked = k.activationStatus === 'revoked';

                return (
                  <div key={k._id} style={{ background:'#fff', borderRadius:12, border:'1px solid #f1f5f9', padding:'16px 18px', opacity:isRevoked?0.6:1 }}>
                    {/* Header */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:36, height:36, borderRadius:8, background: isActive?'#e1f5ee':'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <Key size={16} color={isActive?'#0f6e56':'#64748b'}/>
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:'#1e293b' }}>{k.label||KEY_TYPE_LABELS[k.keyType]}</div>
                          <div style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize' }}>{KEY_TYPE_LABELS[k.keyType]} · {k.environment}</div>
                        </div>
                      </div>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:6, fontSize:10, fontWeight:600, background:cfg.bg, color:cfg.color }}>
                        <CfgIcon size={9}/>{cfg.label}
                      </span>
                    </div>

                    {/* License key */}
                    <div style={{ background:'#f8fafc', borderRadius:7, padding:'8px 10px', marginBottom:10, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <code style={{ fontSize:11, color:'#475569', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {k.licenseKey||'—'}
                      </code>
                      {k.licenseKey && <CopyBtn text={k.licenseKey}/>}
                    </div>

                    {/* Meta */}
                    <div style={{ fontSize:11, color:'#94a3b8', display:'flex', flexWrap:'wrap', gap:10, marginBottom:12 }}>
                      {k.validTo && <span>Expires: <strong style={{ color:'#475569' }}>{fmtDate(k.validTo)}</strong></span>}
                      {k.maxUsage && <span>Usage: <strong style={{ color:'#475569' }}>{k.usageCount||0}/{k.maxUsage}</strong></span>}
                      {k.scope?.length>0 && <span>Scope: <strong style={{ color:'#475569' }}>{k.scope.slice(0,2).join(', ')}{k.scope.length>2?'…':''}</strong></span>}
                    </div>

                    {/* Actions */}
                    {!isRevoked && (
                      <div style={{ display:'flex', gap:6 }}>
                        {isActive && (
                          <button onClick={() => { setActiveKey(k); setModal('renew'); }}
                            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'7px', background:'#e1f5ee', border:'1px solid #9fe1cb', borderRadius:7, fontSize:12, cursor:'pointer', color:'#0f6e56', fontWeight:500 }}>
                            <RotateCcw size={12}/>Renew
                          </button>
                        )}
                        <button onClick={() => { setActiveKey(k); setModal('revoke'); }}
                          style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'7px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:7, fontSize:12, cursor:'pointer', color:'#ef4444', fontWeight:500 }}>
                          <Trash2 size={12}/>Revoke
                        </button>
                      </div>
                    )}
                    {isRevoked && k.revokedReason && (
                      <div style={{ fontSize:11, color:'#94a3b8', fontStyle:'italic' }}>Revoked: {k.revokedReason}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

      {/* Pagination */}
      {pagination.pages>1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:20 }}>
          <button disabled={filters.page<=1} onClick={() => setFilter('page', filters.page-1)}
            style={{ padding:'6px 12px', border:'1px solid #e2e8f0', borderRadius:7, background:'#fff', cursor:'pointer', fontSize:12 }}>← Prev</button>
          <span style={{ padding:'6px 12px', fontSize:12, color:'#64748b' }}>{filters.page} / {pagination.pages}</span>
          <button disabled={filters.page>=pagination.pages} onClick={() => setFilter('page', filters.page+1)}
            style={{ padding:'6px 12px', border:'1px solid #e2e8f0', borderRadius:7, background:'#fff', cursor:'pointer', fontSize:12 }}>Next →</button>
        </div>
      )}

      {/* Modals */}
      {modal==='generate' && (
        <GenerateKeyModal userId={userId} onGenerate={handleGenerate} onClose={() => setModal(null)} isSaving={isSaving}/>
      )}
      {modal==='renew' && activeKey && (
        <RenewModal keyItem={activeKey} userId={userId} onRenew={handleRenew} onClose={() => { setModal(null); setActiveKey(null); }} isSaving={isSaving}/>
      )}
      {modal==='revoke' && activeKey && (
        <RevokeModal keyItem={activeKey} onRevoke={handleRevoke} onClose={() => { setModal(null); setActiveKey(null); }} isSaving={isSaving}/>
      )}
    </PageShell>
  );
};
export default KeyWallet;